import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import BackButton from "@/components/BackButton";
import { MessageCircle, Mail, Instagram, Twitch, Facebook, Youtube, Link as LinkIcon } from "lucide-react";

const digits = (s) => (s || "").replace(/[^\d]/g, "");
const asUrl = (v, base) => {
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v;
  return base ? base + v.replace(/^@/, "") : v;
};

const CHANNELS = [
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, color: "text-green-400", href: (v) => `https://wa.me/${digits(v)}` },
  { key: "email", label: "Email", icon: Mail, color: "text-yellow-400", href: (v) => `mailto:${v}` },
  { key: "instagram", label: "Instagram", icon: Instagram, color: "text-pink-400", href: (v) => asUrl(v, "https://instagram.com/") },
  { key: "twitch", label: "Twitch", icon: Twitch, color: "text-purple-400", href: (v) => asUrl(v, "https://twitch.tv/") },
  { key: "facebook", label: "Facebook", icon: Facebook, color: "text-blue-400", href: (v) => asUrl(v, "https://facebook.com/") },
  { key: "youtube", label: "YouTube", icon: Youtube, color: "text-red-400", href: (v) => asUrl(v, "https://youtube.com/") },
];

export default function Contacts() {
  const [c, setC] = useState(null);

  useEffect(() => {
    api.get("/contacts").then((r) => setC(r.data)).catch(() => setC({}));
  }, []);

  const items = (c ? CHANNELS.filter((ch) => (c[ch.key] || "").trim()) : []);
  const custom = (c?.custom || []).filter((x) => (x.label || x.value));
  const empty = c && items.length === 0 && custom.length === 0;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
      <BackButton className="mb-6" />
      <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tighter text-zinc-50">Contatti</h1>
      <p className="mt-2 text-zinc-400">Contatta l'amministratore di Team Tuono sui canali qui sotto.</p>

      {!c && <p className="mt-10 text-zinc-500">Caricamento…</p>}
      {empty && <p className="mt-10 text-zinc-500" data-testid="contacts-empty">Nessun contatto disponibile al momento.</p>}

      <div className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="contacts-grid">
        {items.map((ch, i) => {
          const val = c[ch.key];
          const Icon = ch.icon;
          return (
            <motion.a key={ch.key} href={ch.href(val)} target="_blank" rel="noreferrer"
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              data-testid={`contact-${ch.key}`}
              className="flex items-center gap-4 border border-zinc-800 bg-zinc-900/50 px-5 py-4 hover:border-zinc-600 hover:bg-zinc-900 transition-colors duration-200 group">
              <Icon className={`h-6 w-6 ${ch.color} shrink-0`} />
              <div className="min-w-0">
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500">{ch.label}</div>
                <div className="font-heading font-bold text-zinc-100 truncate group-hover:text-yellow-400 transition-colors duration-200">{val}</div>
              </div>
            </motion.a>
          );
        })}
        {custom.map((x, i) => {
          const href = /^https?:\/\//i.test(x.value) ? x.value : (x.value?.includes("@") ? `mailto:${x.value}` : null);
          const inner = (
            <>
              <LinkIcon className="h-6 w-6 text-cyan-400 shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500">{x.label || "Contatto"}</div>
                <div className="font-heading font-bold text-zinc-100 truncate">{x.value}</div>
              </div>
            </>
          );
          const cls = "flex items-center gap-4 border border-zinc-800 bg-zinc-900/50 px-5 py-4 hover:border-zinc-600 transition-colors duration-200";
          return href ? (
            <a key={`c-${i}`} href={href} target="_blank" rel="noreferrer" className={cls} data-testid={`contact-custom-${i}`}>{inner}</a>
          ) : (
            <div key={`c-${i}`} className={cls} data-testid={`contact-custom-${i}`}>{inner}</div>
          );
        })}
      </div>
    </div>
  );
}
